import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {BackendMessageResponse, BackendNoResponse, SupportCase} from "./model";
import {useParams, useSearchParams} from "react-router-dom";
import {CaseMessage, CaseMessageRole, CaseMessageType, NoResponse} from "./Conversation";
import {FetchError, toRole} from "./utils";
import {useAuth} from "../auth/AuthHook";
import {useErrorReporter} from "../general/ErrorViewer";
import {Websocket, WebsocketBuffer, WebsocketBuilder} from "websocket-ts";
import {basePath, withBase} from "../utils/basePath";
import {CaseRenamingEndEvent} from "../events/ProgressEvents";

const emptyCase = () => {
    return {
        case_id: null,
        org_id: "",
        title: "",
        severity: "",
        created_by: "",
        status: "",
        messages: []
    } as SupportCase
}

export interface SupportCaseState {
    supportCase?: SupportCase
    isLoading: boolean
    isSyncing: boolean
    progress: Map<number, WsEvent[]>
    error?: string
}

type EventCallback = (e: ContextualEvent) => Promise<void>

interface CaseStateMapContextType {
    caseStateMap: Map<string, SupportCaseState>
    setCaseStateMap: (caseStateMapSetter: (prevState: Map<string, SupportCaseState>) => Map<string, SupportCaseState>) => void,
    webSocket?: Websocket,
    webSocketReadyState?: number,
    webSocketEnsureConnected: () => void,
    subscribeToEvents: (f: EventCallback) => (() => void),
}

interface SendingLimitsType {
    limit: number,
    period: number,
    remaining: number,
    cooldown: number,
    lastUpdated?: number
}

interface SendingLimitsContextType {
    limits: SendingLimitsType
    setLimits: (limitsSetter: (prevState: SendingLimitsType) => SendingLimitsType) => void
}

export interface CaseNavigationStateBase {
    preventReload?: boolean
}

export const CaseStateMapContext = createContext<CaseStateMapContextType | undefined>(undefined)
export const SendingLimitsContext = createContext<SendingLimitsContextType>({
    limits: {
        limit: 0,
        period: 0,
        remaining: -1,
        cooldown: 0,
        lastUpdated: undefined
    },
    setLimits: () => {
        throw new Error("setLimits must be used within a SendingLimitsProvider")
    }
})

const useCaseStateContext = () => {
    const context = useContext(CaseStateMapContext)
    if (context === undefined) {
        throw new Error('useCaseStateContext must be used within a CaseStateMapProvider')
    }
    return context
}

export interface WsEvent {
    position: "start" | "progress" | "end"
    activity: string
    json_name: string
}

export interface EventWrapper {
    org_id: string
    json_name: string
}

export interface ReadyForSendingEvent extends EventWrapper {
}

export interface ContextualEvent extends EventWrapper {
    case_id: string
    index: number
    event: WsEvent
}

const WS_TIMEOUT = 120 * 1000;

export const CaseStateMapProvider: React.FC<React.PropsWithChildren<{urlFn?: () => URL, backoffBase?: number}>> = (
    {
        children,
        urlFn = () => new URL(window.location.href),
        backoffBase = 2000
    }) => {
    const urlWithoutPath = new URL(`${urlFn().protocol}//${urlFn().host}`)
    const [caseStateMap, setCaseStateMap] = useState<Map<string, SupportCaseState>>(new Map())
    const {getHeaders, isLoggedIn, getUserId} = useAuth()
    const [backendUrl, setBackendUrl] = useState<URL>(urlWithoutPath)
    const [webSocket, setWebSocket] = useState<[Websocket, number] | undefined>(undefined)
    const [webSocketCounter, setWebSocketCounter] = useState(0)
    const [intendedState, setIntendedState] = useState<"connected" | "disconnected">("disconnected")
    const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [callbacks, setCallbacks] = useState<EventCallback[]>([])

    const handleReceivedRename = useCallback(async (event: ContextualEvent) => {
        console.log(`Received event: ${event}`);
        if (event.event.activity === "renaming" && event.event.position === "end") {
            const renameEndEvent = event.event as CaseRenamingEndEvent
            const title = renameEndEvent.title
            setCaseStateMap(prevState => {
                const caseId = event.case_id
                const prevCaseState = prevState.get(caseId);
                if (prevCaseState === undefined) {
                    return prevState
                }
                const newCaseState = {
                    ...prevCaseState,
                    supportCase: {
                        ...prevCaseState.supportCase,
                        title: title
                    } as SupportCase
                }
                const res = new Map(prevState);
                res.set(caseId, newCaseState)
                return res
            })
        }
    }, [])

    if (!callbacks.includes(handleReceivedRename)) {
        setCallbacks(prev => [...prev, handleReceivedRename])
    }

    const resetWsTimeout = useCallback(() => {
        if (disconnectTimerRef.current !== null) {
            clearTimeout(disconnectTimerRef.current)
        }
        const t = setTimeout(() => {
            console.log("Disconnecting websocket due to inactivity ...")
            setIntendedState("disconnected")
        }, WS_TIMEOUT)
        disconnectTimerRef.current = t
        return t
    }, [])

    if (backendUrl.href != urlWithoutPath.href) {
        setBackendUrl(urlWithoutPath)
    }
    const handleWsEvent = useCallback(async (event: EventWrapper, ws: Websocket) => {
        console.log(`Received websocket event: ${JSON.stringify(event)}`)
        resetWsTimeout()
        setWebSocket((prevState) => {
            let newState = prevState
            if (prevState !== undefined && prevState[0] === ws) {
                newState = [ws, ws.readyState]
            }
            return newState
        })
        const contextEvent = event as ContextualEvent
        if (contextEvent.event === undefined) {
            return
        }

        console.log(`Invoking callbacks (#${callbacks.length})`)
        for (const cb of callbacks) {
            await cb(contextEvent)
        }

        setCaseStateMap(prevState => {
            const caseId = contextEvent.case_id
            const prevCaseState = prevState.get(caseId);
            if (prevCaseState === undefined) {
                return prevState
            }

            const progress = new Map(prevCaseState.progress)
            const activity_events = progress.get(contextEvent.index) || []
            activity_events.push(contextEvent.event)
            progress.set(contextEvent.index, activity_events)
            const newCaseState = {
                ...prevCaseState,
                progress: progress
            }
            const res = new Map(prevState);
            res.set(caseId, newCaseState)
            // console.log(`Checking event: ${JSON.stringify(contextEvent)}`)
            // if (contextEvent.event.activity === "renaming" && contextEvent.event.position === "end") {
            //     console.log(`Renaming ${contextEvent.case_id}`)
            //     const renameEndEvent = contextEvent.event as CaseRenamingEndEvent
            //     const newTitle = renameEndEvent.title
            //     const caseState = res.get(contextEvent.case_id)
            //     if (caseState != null && caseState.supportCase != null) {
            //         caseState.supportCase.title = newTitle
            //         res.set(contextEvent.case_id, caseState)
            //     }
            // }

            return res
        })
    }, [callbacks, resetWsTimeout])

    const updateWebSocketTo = useCallback((newWS: Websocket | undefined) => {
        setWebSocket(newWS == undefined ? undefined : [newWS, newWS.readyState])
    }, [])

    useEffect(() => {
        let wsCreated: Websocket | undefined = undefined
        let cancelled = false

        const connectWs = async () => {
            try {
                console.log(`Websocket creation round ${webSocketCounter} current time is ${Date.now()}`)
                if (!isLoggedIn()) {
                    return
                }
                const host = backendUrl.host
                const isSecure = backendUrl.protocol.startsWith("https")
                const headers = await getHeaders()
                const token = headers["Authorization"]
                if (!token) {
                    console.log("No auth token for websocket connection, not connecting")
                    return
                }
                if (intendedState === "disconnected" || !isLoggedIn() ) {
                    updateWebSocketTo(undefined)
                    return
                }
                const ws = new WebsocketBuilder(`${isSecure ? "wss" : "ws"}://${host}${basePath}/api/observer/ws`)
                    .onOpen((e) => {
                        console.log(`Websocket opened in case hook, readyState: ${e.readyState}`)
                        updateWebSocketTo(e)
                    })
                    .onClose((ws2, err) => {
                        console.log(`Websocket closed in case hook ${err.code}: "${err.reason}", readyState: ${ws2.readyState}, cancelling: ${cancelled}`)
                        setWebSocket((prevState) => {
                            let newState = prevState
                            if (prevState?.[0] === ws2) {
                                newState = [ws2, ws2.readyState]
                            }
                            return newState
                        })

                    })
                    .withMaxRetries(0)
                    .withInstantReconnect(false)
                    .withBuffer(new ListBuffer())
                    .onMessage(async (ws2, e) => {
                        const json_text = e.data as string
                        const message = JSON.parse(json_text) as EventWrapper
                        await handleWsEvent(message, ws2)
                    })
                    .onError((ws2, event) => console.log(`Websocket error in case hook, readyState: ${ws2.readyState}, event properties: ${Object.keys(event).join(", ")}, event type: ${event.type}`))
                    .build()
                console.log(`Created ws, state: ${ws.readyState}`)
                updateWebSocketTo(ws)
                ws.send(JSON.stringify({json_name: "client_ready_event", token: token}))
                wsCreated = ws
            } catch (e) {
                console.log(`Error getting headers: ${e}`)
                setWebSocket(_ => undefined)
            }
        }
        const t = resetWsTimeout()
        connectWs()
        return () => {
            cancelled = true
            wsCreated?.close()
            clearTimeout(t)
        }
    }, [isLoggedIn, handleWsEvent, backendUrl, webSocketCounter, getUserId, getHeaders, intendedState, resetWsTimeout, updateWebSocketTo])

    const webSocketEnsureConnected = useCallback(() => {
        resetWsTimeout()
        setIntendedState("connected")
    }, [resetWsTimeout])

    const subscribeToEvents = useCallback((callback: EventCallback) => {
        setCallbacks((prev) => [...prev, callback])
        return () => {
            setCallbacks(prev => prev.filter(cb => cb !== callback))
        }
    }, [])

    return (
        <CaseStateMapContext.Provider value={{
            caseStateMap,
            setCaseStateMap,
            webSocket: webSocket?.[0],
            webSocketReadyState: webSocket?.[1] ?? WebSocket.CLOSED,
            webSocketEnsureConnected: webSocketEnsureConnected,
            subscribeToEvents: subscribeToEvents
        }}>
            {children}
        </CaseStateMapContext.Provider>
    )
}

export const SendingLimitsProvider: React.FC<React.PropsWithChildren<{}>> = ({children}) => {
    const [limits, setLimits] = useState<SendingLimitsType>({
        limit: 0,
        period: 0,
        remaining: -1,
        cooldown: 0,
        lastUpdated: undefined
    })
    return (
        <SendingLimitsContext.Provider value={{
            limits,
            setLimits
        }}>
            {children}
        </SendingLimitsContext.Provider>
    )
}

export const useSupportCase = () => {
    const {caseId} = useParams()
    const [searchParams] = useSearchParams();
    const requestedOrgId = searchParams.get("org_id")
    const {getHeaders, isLoggedIn, setOrganization, activeOrganization} = useAuth()
    const reporter = useErrorReporter()
    const {caseStateMap, setCaseStateMap, webSocket, webSocketReadyState, webSocketEnsureConnected, subscribeToEvents} = useCaseStateContext()
    const [lastCaseIdLoaded, setLastCaseIdLoaded] = useState<string | undefined>(undefined)
    const {limits, setLimits} = useContext(SendingLimitsContext)

    useEffect(() => {
        if (requestedOrgId && requestedOrgId !== activeOrganization) {
            setOrganization(requestedOrgId)
        }
    }, [requestedOrgId, activeOrganization, setOrganization])

    const updateSpecificSupportCase = useCallback((targetCaseId: string | undefined, updateFn: (supportCase: SupportCaseState) => SupportCaseState) => {
        setCaseStateMap(prevState => {
            if (targetCaseId == null) {
                return prevState
            }
            const prevCaseState = prevState.get(targetCaseId) || {
                supportCase: emptyCase(),
                isLoading: false,
                isSyncing: false,
                progress: new Map()
            }
            const newCaseState = updateFn(prevCaseState)
            const res = new Map(prevState);
            res.set(targetCaseId, newCaseState)
            return res
        })
    }, [setCaseStateMap])

    const updateSupportCase = useCallback((updateFn: (supportCase: SupportCaseState) => SupportCaseState) => {
        return updateSpecificSupportCase(caseId, updateFn)
    }, [caseId, updateSpecificSupportCase])

    const fetchData = useCallback(async (url: string, method: string, body?: any) => {
        try {
            const headers = await getHeaders();
            console.log(`Fetching ${method} ${url} with active organization ${headers["Active-Organization"]}`)
            const resp = await fetch(withBase(url), {
                method: method,
                credentials: "same-origin",
                headers: {
                    ...headers,
                    "Content-Type": "application/json"
                },
                body: body == undefined ? undefined : JSON.stringify(body)
            })
            if (!resp.ok) {
                if (resp.status === 429) {
                    setLimits((prevState) => {
                        return {
                            limit: Number(resp.headers.get("x-ratelimit-limit-requests")!) ?? prevState.limit,
                            period: Number(resp.headers.get("x-ratelimit-period-requests")!) ?? prevState.period,
                            remaining: 0,
                            cooldown: Number(resp.headers.get("x-ratelimit-cooldown-requests")!) ?? prevState.cooldown
                        }
                    })
                }
                const errorText = await resp.text()
                throw new FetchError(`${resp.status} ${resp.statusText} - ${errorText}`, resp)
            }
            setLimits((prevState) => {
                return {
                    limit: Number(resp.headers.get("x-ratelimit-limit-requests")!) ?? prevState.limit,
                    period: Number(resp.headers.get("x-ratelimit-period-requests")!) ?? prevState.period,
                    remaining: Number(resp.headers.get("x-ratelimit-remaining-requests")!) ?? prevState.remaining,
                    cooldown: Number(resp.headers.get("x-ratelimit-cooldown-requests")!) ?? prevState.cooldown,
                    lastUpdated: Date.now()
                }
            })
            return resp
        } catch (e) {
            reporter(`Error in ${method} ${url}: ${e}`)
            throw e
        }
    }, [reporter, getHeaders, setLimits, activeOrganization])

    const fetchCase = useCallback(async () => {
        try {
            updateSupportCase((prevState) => {
                const supportCase = prevState?.supportCase || emptyCase();
                    return {
                        supportCase: supportCase,
                        isLoading: prevState.isLoading,
                        isSyncing: true,
                        progress: prevState.progress,
                    }
            })
            console.log(`Fetching case with id ${caseId} in organization ${activeOrganization} ...`)
            const caseResponse = await fetchData("api/cases/" + caseId, "GET", undefined)
            const caseInfo = await caseResponse.json()

            const messages = caseInfo.messages as (BackendMessageResponse | BackendNoResponse)[];
            console.log(`Received messages: ${messages.length}`)
            const filteredMessages = messages
                .map((m): CaseMessage | NoResponse => {
                    if (m.json_name === "no_response") {
                        return {
                            messageType: CaseMessageType.NO_RESPONSE,
                            messageRole: CaseMessageRole.NO_RESPONSE,
                            reason_code: m.reason_code,
                            displayable_reason: m.displayable_reason,
                            creationTime: m.creation_time,
                        }
                    }
                    return {
                        "text": m.text,
                        "messageType": m.party == "user" ? CaseMessageType.USER_MESSAGE : CaseMessageType.MESSAGE_RESPONSE,
                        "messageRole": toRole(m.role, m.party),
                        "requestedBy": m?.user_id,
                        "timeToRespond": m?.time_to_respond,
                        "creationTime": m?.creation_time,
                        "attachment_id": m?.attachment_id
                    } as CaseMessage
                })
                .filter(m => m.messageRole !== CaseMessageRole.SUMMARY)
            console.log(`Filtered messages: ${filteredMessages.length}`)
            updateSupportCase((prevState) => {
                return {
                    supportCase: {
                        ...caseInfo,
                        "messages": filteredMessages
                    },
                    isLoading: prevState?.isLoading || false,
                    isSyncing: false,
                    progress: new Map(),
                }
            })
        } catch (e) {
            console.log(`Error fetching case: ${e}`)
            updateSupportCase((prevState) => {
                return {
                    supportCase: prevState?.supportCase || emptyCase(),
                    isLoading: prevState.isLoading,
                    isSyncing: false,
                    progress: new Map(),
                }
            })
        }
    }, [updateSupportCase, fetchData, caseId, activeOrganization])

    const deleteSupportCase = useCallback(async () => {
        setCaseStateMap(prevState => {
            if (caseId === null || caseId === undefined) {
                return prevState
            }
            const res = new Map(prevState);
            res.delete(caseId)
            return res
        })
    }, [caseId, setCaseStateMap])


    useEffect(() => {
        if (!isLoggedIn() && caseStateMap.size > 0) {
            setCaseStateMap(() => new Map())
            setLastCaseIdLoaded(undefined)
        }
    }, [isLoggedIn, caseStateMap, setCaseStateMap]);

    useEffect(() => {
        if (!isLoggedIn()) {
            return
        }
        if (caseId === null || caseId === undefined) {
            return
        }
        if (requestedOrgId && requestedOrgId !== activeOrganization) {
            return
        }
        if (caseId === lastCaseIdLoaded) {
            return
        }
        setLastCaseIdLoaded(caseId)
        if (caseStateMap.has(caseId) && caseStateMap.get(caseId)?.isLoading === true) {
            return
        }
        fetchCase()
    }, [caseId, lastCaseIdLoaded, fetchCase, caseStateMap, isLoggedIn, requestedOrgId, activeOrganization])

    const setWaitingForResponse = useCallback((waiting: boolean) => {
        updateSupportCase((prevState) => {
            return {
                ...prevState,
                isLoading: waiting
            }
        })
    }, [updateSupportCase])

    const addNewMessage = useCallback((msg: CaseMessage) => {
        updateSupportCase((c) => {
            return {
                ...c,
                supportCase: {
                    ...c.supportCase!,
                    messages: [...c.supportCase?.messages ?? [], msg]
                }
            } as SupportCaseState
        })
    }, [updateSupportCase])

    return useMemo(() => {
        const caseState = caseId != undefined ? caseStateMap.get(caseId) : undefined
        return {
            supportCaseState: caseState,
            updateSupportCase: updateSupportCase,
            updateSpecificSupportCase: updateSpecificSupportCase,
            deleteSupportCase: deleteSupportCase,
            caseId: caseId,
            caseStateMapSize: caseStateMap.size,
            limits: limits,
            fetchData: fetchData,
            webSocket: webSocket,
            webSocketReadyState: webSocketReadyState,
            webSocketEnsureConnected: webSocketEnsureConnected,
            setWaitingForResponse: setWaitingForResponse,
            subscribeToEvents: subscribeToEvents,
            addNewMessage: addNewMessage,
            caseStateMap: caseStateMap,
            setCaseStateMap: setCaseStateMap,
            fetchCase: fetchCase,
        }
    }, [caseId, caseStateMap, updateSupportCase, updateSpecificSupportCase, deleteSupportCase, limits, fetchData, webSocket, webSocketReadyState, webSocketEnsureConnected, setWaitingForResponse, subscribeToEvents, addNewMessage, setCaseStateMap])
}


class ListBuffer implements WebsocketBuffer {
    private buffer: string[] = []

    add(data: string): void {
        this.buffer.push(data)
    }

    read(): string | ArrayBufferLike | Blob | ArrayBufferView | undefined {
        return this.buffer.shift()
    }
}
